import React from 'react'
import { EffectReducer } from 'use-effect-reducer'
import { isCancellation } from '../../helpers/errorChecking'
import { getConnectionErrorMessage } from '../../helpers/getConnectionErrorMessage'
import { LSPAYErrorMessage } from '../../helpers/lspay.types'
import {
  LSPAY_ERROR,
  LSPAY_ERROR_MESSAGES,
  PAIRING_ERROR,
  STRIPE_ERROR_CODE,
  SWIPER_ERROR_CODES,
} from '../../helpers/lspayConstants'
import { getEffectForRetry } from './effects'
import { RefundEffect, RefundEvent, RefundState } from './types'

export const refundStateReducer: EffectReducer<
  RefundState,
  RefundEvent,
  RefundEffect
> = (state, event, exec) => {
  switch (event.type) {
    case 'CARD_NOT_PRESENT_OK': {
      if (state.status !== 'refundingViaCardNotPresent') return state

      return {
        retailerPaymentType: state.retailerPaymentType,
        errorMessage: undefined,
        transaction: event.transaction,
        status: 'done',
      }
    }
    case 'CARD_NOT_PRESENT_ERROR': {
      if (state.status !== 'refundingViaCardNotPresent') return state

      const { error } = event
      const { name: retailerPaymentTypeName } = state.retailerPaymentType

      let errorMessage: LSPAYErrorMessage | undefined =
        LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_REFUND]
      let retryable = true

      if (
        error.code ===
          SWIPER_ERROR_CODES.REFUND_AMOUNT_MORE_THAN_ORIGINAL_TRANSACTION &&
        error.fields?.refundable_amount
      ) {
        errorMessage = {
          header: `This refund with ${retailerPaymentTypeName} can't be processed.`,
          body: (
            <span>
              {`The total original amount paid by ${retailerPaymentTypeName} on this sale was `}
              <strong>{`$${error.fields.refundable_amount}`}</strong>
              {`. You can refund up to this amount with ${retailerPaymentTypeName}, then refund the balance with another payment type.`}
            </span>
          ),
          action: 'Got it, thanks!',
        }
        retryable = false
      }
      if (error.code === SWIPER_ERROR_CODES.LSPAY_ZERO_DOLLAR_REFUNDABLE) {
        errorMessage = {
          header: `This refund with ${retailerPaymentTypeName} can't be processed.`,
          body: `The total original amount paid by ${retailerPaymentTypeName} on this sale has been refunded. Choose another payment type to refund the balance`,
        }
        retryable = false
      }

      if (
        error.code ===
          SWIPER_ERROR_CODES.LSPAY_CARD_NOT_PRESENT_INTERAC_REFUND &&
        error.fields?.charge_id
      ) {
        return {
          status: 'interacPresentPrompt',
          chargeId: error.fields.charge_id,
          retailerPaymentType: state.retailerPaymentType,
          registerId: event.registerId,
          errorMessage: undefined,
        }
      }

      return {
        ...state,
        errorMessage,
        retryable,
      }
    }
    case 'CONTINUE_WITH_CARD_PRESENT': {
      if (state.status !== 'interacPresentPrompt') {
        return state
      }

      exec({ type: 'fetchPairingEffect' })

      return {
        status: 'fetchingPairing',
        retailerPaymentType: state.retailerPaymentType,
        refundMethod: 'interac',
        chargeId: state.chargeId,
        errorMessage: undefined,
      }
    }
    case 'PAIRING_OK': {
      if (state.status !== 'fetchingPairing') return state

      exec({
        type: 'connectTerminalEffect',
        isSimulated: event.pairing.isSimulated,
        pairingSN: event.pairing.sn,
        terminalID: event.pairing.terminalID,
        terminalModel: event.pairing.terminalModel,
      })

      return {
        status: 'connectingToTerminal',
        pairing: event.pairing,
        chargeId: state.chargeId,
        refundMethod: state.refundMethod,
        retailerPaymentType: state.retailerPaymentType,
        errorMessage: undefined,
      }
    }
    case 'PAIRING_ERROR': {
      if (state.status !== 'fetchingPairing') return state

      // Distinguish network errors (HTTP 4xx or 5xx) from pairing issues
      const errorMessage = event.pairingIsMissing
        ? {
            header: 'The register is not paired with a terminal.',
            body: `To make a refund with "${state.retailerPaymentType.name}", pair this register with a terminal in your payment type setup. To complete this refund choose another payment type.`,
          }
        : LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_REFUND]

      return {
        ...state,
        errorMessage,
        retryable: !event.pairingIsMissing,
      }
    }
    case 'TERMINAL_OK': {
      if (state.status !== 'connectingToTerminal') return state

      const { terminal, terminalMonitoringInstance } = event
      const { retailerPaymentType, refundMethod, chargeId } = state

      exec({
        type: 'cardPresentEffect',
        terminal,
        terminalMonitoringInstance,
        chargeId,
        isRetry: false,
      })

      return {
        status: 'refundingViaCardPresent',
        retailerPaymentType,
        refundMethod,
        terminal,
        terminalMonitoringInstance,
        chargeId,
        errorMessage: undefined,
      }
    }
    case 'TERMINAL_ERROR': {
      if (state.status !== 'connectingToTerminal') return state

      const { error } = event
      const code = error.code || error.error?.code || error.name

      const retryable =
        code !== STRIPE_ERROR_CODE.FAILED_FETCH_CONNECTION_TOKEN &&
        code !== PAIRING_ERROR

      return {
        ...state,
        errorMessage: getConnectionErrorMessage({
          error,
          pairingLabel: state.pairing.label || 'no label',
          paymentTypeName: state.retailerPaymentType.name,
          isRefund: true,
        }),
        retryable,
      }
    }
    case 'CARD_PRESENT_OK': {
      if (state.status !== 'refundingViaCardPresent') return state

      exec({
        type: 'persistCardPresentEffect',
        refundMethod: state.refundMethod,
      })

      return {
        status: 'persistingCardPresentRefund',
        refundMethod: state.refundMethod,
        retailerPaymentType: state.retailerPaymentType,
        errorMessage: undefined,
      }
    }
    case 'CARD_PRESENT_ERROR': {
      if (state.status !== 'refundingViaCardPresent') return state

      return {
        ...state,
        errorMessage: isCancellation(event.error)
          ? LSPAY_ERROR_MESSAGES[LSPAY_ERROR.CANCEL_REFUND]
          : LSPAY_ERROR_MESSAGES[LSPAY_ERROR.DEFAULT_REFUND],
        retryable: true,
      }
    }
    case 'PERSISTING_CARD_PRESENT_OK': {
      if (state.status !== 'persistingCardPresentRefund') return state

      return {
        status: 'done',
        transaction: event.transaction,
        errorMessage: undefined,
      }
    }
    case 'RETRY': {
      // retry disabled ? do nothin
      if (!state.retryable) return state

      const effect = getEffectForRetry(state)

      if (effect) exec(effect)

      return {
        ...state,
        // clear error-state-specific properties
        errorMessage: undefined,
      }
    }
    default: {
      // This `never` typing will let us know at compile time if we do not handle an existing event type. Neat !
      const _exhaustiveCheck: never = event
      return _exhaustiveCheck
    }
  }
}
